import i18n from 'i18next';
/**
 * payment.storeName 인코딩 유틸 — 장소 태그 + 카테고리 + 메모 를
 * 단일 string 컬럼에 보관하는 v1 인코딩.
 *
 * 인코딩 포맷: "[<tag>] <baseName> (<memo>)"
 *  · tag: 'main' | 'sub:{subPlaceId}' | 'etc'
 *  · main 은 prefix 생략. memo 없으면 () 생략.
 *
 * 구버전 포맷 호환: [1차]→main / [2차]→첫 sub_place / [3차]→둘째 sub_place / [기타]→etc.
 */

export interface ParsedStoreName {
    /** 장소 태그 — 'main' | `sub:{subPlaceId}` | 'etc' */
    placeTag: string;
    /** 카테고리/항목명만 (prefix·메모 제거) */
    cleanName: string;
    /** 메모 — 괄호 안 텍스트 */
    memo: string;
}

export const parseStoreName = (
    raw: string | undefined,
    sortedSubPlacesById: number[] = [], // index 0 = 2차 sub_place id, index 1 = 3차 ...
): ParsedStoreName => {
    const text = (raw || '').trim();
    if (!text) return { placeTag: 'main', cleanName: '', memo: '' };
    let work = text;
    let placeTag = 'main';
    const tagMatch = work.match(/^\[([^\]]+)\]\s*(.*)$/);
    if (tagMatch) {
        const t = tagMatch[1];
        work = tagMatch[2];
        if (t === 'main' || t === 'etc' || t.startsWith('sub:')) {
            placeTag = t;
        } else if (t === '1차') placeTag = 'main';
        else if (t === '기타') placeTag = 'etc';
        else if (t === '2차' && sortedSubPlacesById[0] != null) placeTag = `sub:${sortedSubPlacesById[0]}`;
        else if (t === '3차' && sortedSubPlacesById[1] != null) placeTag = `sub:${sortedSubPlacesById[1]}`;
        else placeTag = 'etc'; // 알 수 없는 태그 — fallback
    }
    let memo = '';
    const memoMatch = work.match(/^(.*?)\s*\(([^()]+)\)\s*$/);
    if (memoMatch) {
        work = memoMatch[1];
        memo = memoMatch[2];
    }
    return { placeTag, cleanName: work.trim(), memo };
};

/** 표시용 — 카테고리/항목명 (prefix·메모 제거) */
export const cleanStoreNameForDisplay = (raw?: string): string => {
    const parsed = parseStoreName(raw);
    return parsed.cleanName || i18n.t('meeting.expenseFallback');
};

export interface PaymentDisplayContext {
    mainPlaceName?: string;
    subPlaces?: Array<{ subPlaceId: number; placeName: string; sortOrder?: number; category?: string }>;
}

/**
 * "한정식맛집 · 식사비" / "기타 · 교통비" 같은 한 줄 요약 — 결제 list 의 한 줄 표시에 사용.
 * placeName 이 비었으면 placeLabel 로 대체.
 */
export const formatPaymentLine = (
    raw: string | undefined,
    ctx: PaymentDisplayContext = {},
): { placeLabel: string; placeName: string; cleanName: string; memo: string; oneLine: string } => {
    const subPlaces = (ctx.subPlaces || [])
        .filter(sp => sp.category !== 'HOTEL')
        .slice()
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    const sortedIds = subPlaces.map(sp => sp.subPlaceId);
    const parsed = parseStoreName(raw, sortedIds);

    let placeLabel = '';
    let placeName = '';
    if (parsed.placeTag === 'main') {
        placeLabel = i18n.t('meeting.roundOrdinal', { n: 1 });
        placeName = ctx.mainPlaceName || i18n.t('route.mainPlace');
    } else if (parsed.placeTag === 'etc') {
        placeLabel = i18n.t('meeting.etcLabel');
        placeName = '';
    } else if (parsed.placeTag.startsWith('sub:')) {
        const id = Number(parsed.placeTag.slice(4));
        const idx = subPlaces.findIndex(sp => sp.subPlaceId === id);
        if (idx >= 0) {
            placeLabel = i18n.t('meeting.roundOrdinal', { n: idx + 2 });
            placeName = subPlaces[idx].placeName || '';
        } else {
            placeLabel = i18n.t('meeting.etcLabel');
            placeName = '';
        }
    }

    const display = [placeName, parsed.cleanName].filter(Boolean).join(' · ');
    return {
        placeLabel,
        placeName,
        cleanName: parsed.cleanName,
        memo: parsed.memo,
        oneLine: display || i18n.t('meeting.expenseFallback'),
    };
};
